import { useRequiredContext } from "@redotech/react-util/context";
import type { ReclaimActivationSurface } from "@redotech/redo-dispute-settings-common/reclaim-access-settings";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { matchPath, useLocation } from "react-router-dom";
import {
  getTasksCompletedByRoute,
  isGuideComplete,
  isTaskComplete,
} from "../registry/helpers";
import {
  getProductConfig,
  getRegisteredProducts,
} from "../registry/product-registry";
import type {
  AsyncTaskStatuses,
  IntakeAnswers,
  IntakeAnswerValue,
  Product,
  SetupProgress,
  TaskId,
} from "../registry/types";
import { consumeSetupGuideEntrySource } from "./consume-setup-guide-entry-source";
import { deriveShowIntake } from "./derive-show-intake";
import {
  useAnySetupGuideProductEnabled,
  useShopperAgentEnabled,
} from "./feature-flag";
import {
  filterProductsByFeatureFlags,
  pickActiveProduct,
} from "./pick-active-product";
import { detectCompletionTransition } from "./regression-detection";
import {
  SetupGuideContext,
  type SetupGuideContextValue,
} from "./setup-guide-context";
import { getStartGuideResetOptions } from "./start-guide-reset-options";
import { useEntrySourceParam } from "./use-entry-source-param";
import { useSetupProgressQuery } from "./use-setup-progress-query";
import {
  useTaskCompletionEvents,
  type TaskCompletionEvent,
} from "./use-task-completion-events";
import { useWidgetState } from "./use-widget-state";

const EMPTY_ASYNC_TASK_STATUSES: AsyncTaskStatuses = {};

/**
 * Mounted in `RouterRoot` above `<Outlet />`. Whole tree remounts when
 * the URL `:teamId` changes, so this provider naturally re-fetches on
 * team switch without manual key handling.
 *
 * When the feature flag is off, renders children unchanged — no context
 * value is provided, so consumers calling `useSetupGuide()` will throw
 * (defensive — flag-gated UI shouldn't be calling the hook).
 */
export const SetupGuideProvider = memo(function SetupGuideProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const flagEnabled = useAnySetupGuideProductEnabled();
  if (!flagEnabled) {
    return <>{children}</>;
  }
  return <SetupGuideProviderInner>{children}</SetupGuideProviderInner>;
});

const SetupGuideProviderInner = memo(function SetupGuideProviderInner({
  children,
}: {
  children: React.ReactNode;
}) {
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const team = useContext(TeamContext);
  const user = useContext(UserContext);
  const location = useLocation();
  const teamId = team?._id?.toString();
  const userId = user?._id?.toString();

  const { progressByProduct, loading, error, applyRowToCache, refetch } =
    useSetupProgressQuery();
  const shopperAgentProgress = progressByProduct.shopperAgent ?? null;

  const events = useTaskCompletionEvents();

  // Pick the active product via the extracted pure helper.
  const shopperAgentEnabled = useShopperAgentEnabled();
  const shopperAgentRoute =
    shopperAgentEnabled &&
    (!!matchPath("/stores/:storeId/commerce-agent", location.pathname) ||
      !!matchPath("/stores/:storeId/commerce-agent/*", location.pathname) ||
      !!matchPath("/commerce-agent", location.pathname) ||
      !!matchPath("/commerce-agent/*", location.pathname));
  const routePreferredProduct: Product | null = useMemo(() => {
    if (shopperAgentRoute && shopperAgentProgress) {
      return "shopperAgent";
    }
    return null;
  }, [shopperAgentProgress, shopperAgentRoute]);

  const activeProduct: Product | null = useMemo(() => {
    if (!team) {
      return null;
    }
    if (routePreferredProduct) {
      return routePreferredProduct;
    }
    return pickActiveProduct(
      progressByProduct,
      filterProductsByFeatureFlags(getRegisteredProducts(), {
        shopperAgentEnabled,
      }),
    );
  }, [team, progressByProduct, routePreferredProduct, shopperAgentEnabled]);

  const activeProgress: SetupProgress | null = activeProduct
    ? (progressByProduct[activeProduct] ?? null)
    : null;

  // -------- Entry source param (?setupSource=...) -------------------------
  // On first load with the param present, start a fresh progress row with
  // the source recorded for analytics. Skips merchants who are already on
  // the legacy flow — they don't need a row, even if a stale email link
  // drops them here. The skip rule is captured in
  // shouldCreateRowFromEntrySource so it's testable in isolation.
  //
  // Target product is resolved by matching the current pathname against
  // each registered product's `routePattern`. This keeps the URL-param
  // entry path product-agnostic — disputes URLs target disputes, concierge
  // URLs target shopper-agent, etc. Falls back to the active product (if a
  // row already exists) or the first registered product.
  const handleEntrySource = useCallback(
    async (source: string, pathname: string): Promise<boolean> => {
      return consumeSetupGuideEntrySource({
        source,
        pathname,
        team,
        activeProduct,
        progressByProduct,
        startMerchantSetupProgress:
          platformRpcClient.startMerchantSetupProgress,
        applyRowToCache,
        refetchProgress: refetch,
      });
    },
    [
      activeProduct,
      team,
      progressByProduct,
      platformRpcClient,
      applyRowToCache,
      refetch,
    ],
  );
  useEntrySourceParam(handleEntrySource);
  // Row creation is opt-in via an entry source (?setupSource=..., welcome CTA,
  // Shopify install link, widget reminder) or an explicit product CTA calling
  // startGuide. Visiting a product route alone should not open onboarding.

  // -------- Bidirectional regression detection ----------------------------
  // Predicates only read the product's relevantTeamSlice — memoize on
  // (slice, progress) so settings changes outside the slice don't trigger
  // re-evaluation.
  const activeConfig = activeProduct ? getProductConfig(activeProduct) : null;
  const relevantSlice =
    activeConfig && team ? activeConfig.relevantTeamSlice(team) : null;
  const asyncTaskStatusEnabled =
    !!team &&
    !!activeProgress &&
    activeProgress.intakeCompletedAt != null &&
    !!activeConfig?.asyncTaskStatus;
  const asyncTaskStatusQuery = useQuery<AsyncTaskStatuses>({
    queryKey: [
      "setupGuideAsyncTaskStatus",
      teamId,
      activeProduct,
      activeProgress?.id?.toString(),
      activeProgress?.updatedAt?.toISOString(),
    ],
    enabled: asyncTaskStatusEnabled,
    queryFn: async () => {
      if (!team || !activeProgress || !activeConfig?.asyncTaskStatus) {
        return EMPTY_ASYNC_TASK_STATUSES;
      }
      return activeConfig.asyncTaskStatus.load({
        rpcClient: merchantRpcClient,
        team,
        progress: activeProgress,
      });
    },
    // Row writes change the key via updatedAt; keep the last statuses
    // through the refetch instead of collapsing to fallback predicates.
    placeholderData: keepPreviousData,
    refetchInterval: activeConfig?.asyncTaskStatus
      ? (activeConfig.asyncTaskStatus.pollIntervalMs ?? 30_000)
      : false,
  });
  const asyncTaskStatuses =
    asyncTaskStatusQuery.data ?? EMPTY_ASYNC_TASK_STATUSES;
  const asyncTaskStatusesUnresolved =
    asyncTaskStatusEnabled &&
    (asyncTaskStatusQuery.data === undefined ||
      asyncTaskStatusQuery.isPlaceholderData);
  const isCurrentlyLive = useMemo(() => {
    if (!activeConfig || !team || !activeProgress) {
      return false;
    }
    return isGuideComplete(
      activeConfig,
      team,
      activeProgress,
      asyncTaskStatuses,
    );
    // Memo key uses the slice value (not the team object) so unrelated
    // settings updates don't re-fire predicates. `team` itself is also a
    // dep because predicates ultimately read from it; lint can't statically
    // see the slice covers all reads, but we know it does for v1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConfig, activeProgress, relevantSlice, asyncTaskStatuses]);

  const setGuideCompletionMutation = useMutation({
    mutationFn: async (vars: { completed: boolean; product: Product }) => {
      return platformRpcClient.setMerchantSetupGuideCompletion({
        product: vars.product,
        completed: vars.completed,
      });
    },
    onSuccess: (row) => {
      if (row) {
        applyRowToCache(row);
      }
    },
  });
  const setGuideCompletion = useCallback(
    async (product: Product, completed: boolean) => {
      await setGuideCompletionMutation.mutateAsync({ product, completed });
    },
    [setGuideCompletionMutation],
  );

  const lastTransitionFiredAtRef = useRef<{
    product: Product;
    transitionedTo: "complete" | "incomplete";
  } | null>(null);
  useEffect(() => {
    if (!activeProduct || !activeProgress) {
      return;
    }
    const transition = detectCompletionTransition({
      isCurrentlyLive,
      hasPersistedCompletion: activeProgress.completedAt != null,
      asyncStatusesUnresolved: asyncTaskStatusesUnresolved,
    });
    if (!transition) {
      return;
    }
    const last = lastTransitionFiredAtRef.current;
    if (
      last &&
      last.product === activeProduct &&
      last.transitionedTo === transition.transitionedTo
    ) {
      return;
    }
    lastTransitionFiredAtRef.current = {
      product: activeProduct,
      transitionedTo: transition.transitionedTo,
    };
    void setGuideCompletion(
      activeProduct,
      transition.transitionedTo === "complete",
    ).catch(() => {
      lastTransitionFiredAtRef.current = last;
    });
  }, [
    activeProduct,
    activeProgress,
    asyncTaskStatusesUnresolved,
    isCurrentlyLive,
    setGuideCompletion,
  ]);

  // -------- Widget state --------------------------------------------------
  const widget = useWidgetState({
    teamId,
    userId,
    product: activeProduct ?? "disputes",
    progress: activeProgress,
  });

  // -------- Draft intake answers ------------------------------------------
  // Lifted out of the IntakeModal so partial answers survive the modal
  // unmounting + remounting when the merchant navigates between product
  // routes (the host scopes modal rendering by route). Initialized from
  // the persisted row's answers and reset when the active row changes.
  const [intakeAnswers, setIntakeAnswers] = useState<IntakeAnswers>(
    () => activeProgress?.intakeAnswers ?? {},
  );
  const lastActiveProgressIdRef = useRef<bigint | null>(
    activeProgress?.id ?? null,
  );
  useEffect(() => {
    const currentId = activeProgress?.id ?? null;
    if (currentId !== lastActiveProgressIdRef.current) {
      lastActiveProgressIdRef.current = currentId;
      setIntakeAnswers(activeProgress?.intakeAnswers ?? {});
    }
  }, [activeProgress]);
  const setIntakeAnswer = useCallback(
    (questionId: string, value: IntakeAnswerValue) => {
      setIntakeAnswers((prev) => ({ ...prev, [questionId]: value }));
    },
    [],
  );

  // -------- Mutators ------------------------------------------------------
  // All mutations return the updated row and write it directly into the
  // react-query cache, eliminating a follow-up GET roundtrip.

  const submitIntakeMutation = useMutation({
    mutationFn: async (vars: { product: Product; answers: IntakeAnswers }) => {
      return platformRpcClient.submitMerchantSetupIntake({
        product: vars.product,
        answers: vars.answers,
      });
    },
    onSuccess: (row) => {
      if (row) {
        applyRowToCache(row);
      }
    },
  });
  const submitIntake = useCallback(
    async (product: Product, answers: IntakeAnswers) => {
      await submitIntakeMutation.mutateAsync({ product, answers });
    },
    [submitIntakeMutation],
  );

  const [activationSurface, setActivationSurface] =
    useState<ReclaimActivationSurface | null>(null);

  const startGuide = useCallback(
    async (
      product: Product,
      entrySource: string,
      options?: {
        forceResetIntake?: boolean;
        activationSurface?: ReclaimActivationSurface;
      },
    ) => {
      // Session-scoped on purpose. Persisting it would mean a new column, and
      // the channel-derivation PR deliberately carries no CRDB migration. A
      // reload mid-guide loses it and the merchant resolves to the default
      // channel -- which errs in their favour rather than over-billing.
      setActivationSurface(options?.activationSurface ?? null);
      const existingRow = progressByProduct[product] ?? null;
      const resetOptions = getStartGuideResetOptions({
        existingRow,
        forceResetIntake: options?.forceResetIntake === true,
      });
      const row = await platformRpcClient.startMerchantSetupProgress({
        product,
        entrySource,
        ...resetOptions,
      });
      if (row) {
        applyRowToCache(row);
      }
    },
    [applyRowToCache, platformRpcClient, progressByProduct],
  );

  const recordTaskVisited = useCallback(
    (product: Product, taskId: TaskId) => {
      // Fire-and-forget — analytics-only writes never block UX.
      void platformRpcClient
        .recordMerchantSetupTaskVisited({ product, taskId })
        .catch(() => {});
    },
    [platformRpcClient],
  );

  const recordIntakeStep = useCallback(
    (product: Product, setupStepId: string) => {
      // Fire-and-forget — reporting mirror writes should not block modal
      // navigation. The server awaits the local CRDB write and logs failures.
      void platformRpcClient
        .recordMerchantSetupIntakeStep({ product, setupStepId })
        .catch(() => {});
    },
    [platformRpcClient],
  );

  const markTaskCompleteMutation = useMutation({
    mutationFn: async (vars: { product: Product; taskId: TaskId }) => {
      return platformRpcClient.markMerchantSetupTaskComplete({
        product: vars.product,
        taskId: vars.taskId,
      });
    },
    onSuccess: (row) => {
      if (row) {
        applyRowToCache(row);
      }
    },
  });
  const markTaskComplete = useCallback(
    async (product: Product, taskId: TaskId) => {
      await markTaskCompleteMutation.mutateAsync({ product, taskId });
    },
    [markTaskCompleteMutation],
  );

  const routeCompletionInFlightRef = useRef(new Set<string>());
  useEffect(() => {
    if (!activeProduct || !activeConfig || !activeProgress || !team) {
      return;
    }
    const routeCompletedTasks = getTasksCompletedByRoute(
      activeConfig,
      location.pathname,
    ).filter(
      (task) => !isTaskComplete(task, team, activeProgress, asyncTaskStatuses),
    );
    for (const task of routeCompletedTasks) {
      const key = `${activeProduct}:${task.id}:${location.pathname}`;
      if (routeCompletionInFlightRef.current.has(key)) {
        continue;
      }
      routeCompletionInFlightRef.current.add(key);
      void markTaskComplete(activeProduct, task.id).catch(() => {
        routeCompletionInFlightRef.current.delete(key);
      });
    }
  }, [
    activeConfig,
    activeProduct,
    activeProgress,
    asyncTaskStatuses,
    location.pathname,
    markTaskComplete,
    team,
  ]);

  const dismissTaskMutation = useMutation({
    mutationFn: async (vars: { product: Product; taskId: TaskId }) => {
      return platformRpcClient.dismissMerchantSetupTask({
        product: vars.product,
        taskId: vars.taskId,
      });
    },
    onSuccess: (row) => {
      if (row) {
        applyRowToCache(row);
      }
    },
  });
  const dismissTask = useCallback(
    async (product: Product, taskId: TaskId) => {
      await dismissTaskMutation.mutateAsync({ product, taskId });
    },
    [dismissTaskMutation],
  );

  const dismissGuideMutation = useMutation({
    mutationFn: async (vars: { product: Product }) => {
      return platformRpcClient.dismissMerchantSetupGuide({
        product: vars.product,
      });
    },
    onSuccess: (row) => {
      if (row) {
        applyRowToCache(row);
      }
    },
  });
  const dismissGuideForTeam = useCallback(
    async (product: Product) => {
      await dismissGuideMutation.mutateAsync({ product });
    },
    [dismissGuideMutation],
  );

  const deferGuideMutation = useMutation({
    mutationFn: async (vars: { product: Product; untilDate?: Date }) => {
      return platformRpcClient.deferMerchantSetupGuide({
        product: vars.product,
        untilDate: vars.untilDate,
      });
    },
    onSuccess: (row) => {
      if (row) {
        applyRowToCache(row);
      }
    },
  });
  const deferGuideForTeam = useCallback(
    async (product: Product, untilDate?: Date) => {
      await deferGuideMutation.mutateAsync({ product, untilDate });
    },
    [deferGuideMutation],
  );

  const signalTaskCompletion = useCallback(
    (event: TaskCompletionEvent) => {
      events.signal(event);
      // Modal already mutated team/integration state in its own RPC; we
      // need a fresh fetch since predicates read team data outside our
      // cache.
      void refetch();
    },
    [events, refetch],
  );

  // -------- Final value ---------------------------------------------------
  // Derivation is extracted to a pure helper so it's testable in isolation
  // with fake timers. We don't schedule a re-render at deferral expiry —
  // natural re-evaluation (route change, refetch, completion event) is
  // sufficient. The merchant won't sit on a single page through a 7-day
  // window.
  const showIntake = deriveShowIntake(activeProgress, Date.now());

  const value: SetupGuideContextValue = useMemo(
    () => ({
      activeProduct,
      activeProgress,
      asyncTaskStatuses,
      progressByProduct,
      loading,
      showIntake,
      intakeAnswers,
      setIntakeAnswer,
      widget,
      startGuide,
      activationSurface,
      submitIntake,
      setGuideCompletion,
      recordTaskVisited,
      recordIntakeStep,
      markTaskComplete,
      dismissTask,
      dismissGuideForTeam,
      deferGuideForTeam,
      signalTaskCompletion,
      subscribeTaskCompletion: events.subscribe,
    }),
    [
      activeProduct,
      activeProgress,
      asyncTaskStatuses,
      progressByProduct,
      loading,
      showIntake,
      intakeAnswers,
      setIntakeAnswer,
      widget,
      startGuide,
      activationSurface,
      submitIntake,
      setGuideCompletion,
      recordTaskVisited,
      recordIntakeStep,
      markTaskComplete,
      dismissTask,
      dismissGuideForTeam,
      deferGuideForTeam,
      signalTaskCompletion,
      events.subscribe,
    ],
  );

  useEffect(() => {
    if (error) {
      console.warn("setup-guide: progress query failed", error);
    }
  }, [error]);

  return (
    <SetupGuideContext.Provider value={value}>
      {children}
    </SetupGuideContext.Provider>
  );
});
